// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the wrap-up card's words (design/evening-wrapup r2) and the small helpers that put
// numbers, days and times into them.

import type {WrapUpAgentRow} from '@konvo/schema/src/domains/konvo/WrapUp';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export const WRAP_UP_COPY = {
	heading: msg({
		message: 'Wrapping up? Here’s tonight and {day}',
		comment: 'End-of-day wrap-up card in the Konvo chat: heading; {day} is the next working day, e.g. "Friday".',
	}),
	holiday: msg({
		message: 'Tomorrow is {holiday}.',
		comment: 'End-of-day wrap-up card: why the plan is for a later day; {holiday} e.g. "Heritage Day".',
	}),
	agentsTonight: msg({
		message: 'Agents tonight · your machines',
		comment:
			'End-of-day wrap-up card: heading over the coding agents that can work tonight on the person’s own machines.',
	}),
	newTasks: msg({
		message: 'New tasks for you, due {day}',
		comment:
			'End-of-day wrap-up card: heading over new ClickUp tasks for the person; {day} is the next working day, e.g. "Friday".',
	}),
	alsoDue: msg({
		message: 'Also due {day}: {tasks}',
		comment:
			'End-of-day wrap-up card: ClickUp tasks already due on the next working day; {day} e.g. "Friday", {tasks} a comma-separated list of task names.',
	}),
	startBoth: msg({
		message:
			'{agents, plural, one {Start # agent} other {Start # agents}}, {tasks, plural, one {create # task} other {create # tasks}}',
		comment:
			'End-of-day wrap-up card: main button that starts the ticked coding agents and creates the ticked ClickUp tasks.',
	}),
	startAgents: msg({
		message: '{agents, plural, one {Start # agent} other {Start # agents}}',
		comment: 'End-of-day wrap-up card: main button when only coding agents are ticked.',
	}),
	createTasks: msg({
		message: '{tasks, plural, one {Create # task} other {Create # tasks}}',
		comment: 'End-of-day wrap-up card: main button when only new ClickUp tasks are ticked.',
	}),
	nothingTicked: msg({
		message: 'Nothing ticked',
		comment: 'End-of-day wrap-up card: main button (disabled) when every row is unticked.',
	}),
	notTonight: msg({
		message: 'Not tonight',
		comment: 'End-of-day wrap-up card: second button; skips today’s plan only.',
	}),
	newTask: msg({
		message: 'New task · {list}',
		comment:
			'End-of-day wrap-up card: chip on an agent row; Konvo creates this ClickUp task in {list} (e.g. "NFC › Integration").',
	}),
	failedToday: msg({
		message: 'Failed today',
		comment: 'End-of-day wrap-up card: tag on an agent chat that stopped on an error today and is picked up again.',
	}),
	waitingOnYou: msg({
		message: 'Waiting on you',
		comment: 'End-of-day wrap-up card: tag on an agent chat that asked the person something.',
	}),
	runOn: msg({
		message: 'Run on',
		comment: 'End-of-day wrap-up card: heading of the list of the person’s own machines an agent can run on.',
	}),
	online: msg({message: 'Online', comment: 'End-of-day wrap-up card machine list: the machine is connected.'}),
	offlineStarts: msg({
		message: 'Offline · starts when it is back',
		comment:
			'End-of-day wrap-up card machine list: the machine is offline; the agent starts when it reconnects (within 12 hours).',
	}),
	tonightSet: msg({
		message: 'Tonight is set',
		comment: 'End-of-day wrap-up card after Start: everything ticked was started or created.',
	}),
	agentsOn: msg({
		message: '{count, plural, one {# agent on {machines}} other {# agents on {machines}}}',
		comment: 'End-of-day wrap-up card after Start: how many agents run and where; {machines} e.g. "interns".',
	}),
	undo: msg({
		message: 'Undo',
		comment: 'End-of-day wrap-up card after Start: stops the agents and deletes the new tasks (a few minutes only).',
	}),
	working: msg({
		message: '{minutes, plural, one {Working · # min} other {Working · # min}}',
		comment: 'End-of-day wrap-up card after Start: an agent has been working for {minutes} minutes.',
	}),
	workingHours: msg({
		message: 'Working · {hours} h {minutes} min',
		comment:
			'End-of-day wrap-up card after Start: an agent has been working for over an hour, e.g. "Working · 2 h 5 min".',
	}),
	startsAt: msg({
		message: 'Starts {time}',
		comment:
			'End-of-day wrap-up card after Start: the agent waits until {time} (e.g. "18:00"), when the plan limit resets.',
	}),
	waitsFor: msg({
		message: 'Starts when {machine} is back',
		comment: 'End-of-day wrap-up card after Start: the machine is offline; the agent starts when it reconnects.',
	}),
	done: msg({message: 'Done', comment: 'End-of-day wrap-up card after Start: the agent finished.'}),
	didNotStart: msg({
		message: 'Did not start',
		comment: 'End-of-day wrap-up card after Start: the agent could not be started.',
	}),
	stopped: msg({
		message: 'Stopped',
		comment: 'End-of-day wrap-up card after Undo or a stop: the agent no longer runs.',
	}),
	tasksInClickUp: msg({
		message: '{count, plural, one {# task in ClickUp} other {# tasks in ClickUp}}',
		comment: 'End-of-day wrap-up card after Start: how many ClickUp tasks Konvo created, followed by their names.',
	}),
	digest: msg({
		message: '{day} {time}: your agents digest says what they did',
		comment:
			'End-of-day wrap-up card after Start: when the agents digest reports; {day} e.g. "Thursday", {time} e.g. "07:30".',
	}),
	skipped: msg({
		message: 'Not tonight. Konvo asks again on your next working day.',
		comment: 'End-of-day wrap-up card after "Not tonight".',
	}),
	undone: msg({
		message: 'Undone: nothing runs tonight and the new tasks are gone.',
		comment: 'End-of-day wrap-up card after Undo.',
	}),
	gone: msg({message: 'This wrap-up is gone.', comment: 'End-of-day wrap-up card: the plan no longer exists.'}),
	failed: msg({
		message: 'That did not work. Try again in a moment.',
		comment: 'End-of-day wrap-up card: an action on the plan failed.',
	}),
	openInChat: msg({
		message: 'Open in Konvo chat',
		comment: 'Corner pop-up with the end-of-day wrap-up: opens the same card in the person’s Konvo chat.',
	}),
	cornerContext: msg({
		message: 'Konvo · {time}',
		comment: 'Corner pop-up with the end-of-day wrap-up: who and when, e.g. "Konvo · 16:30".',
	}),
} as const;

/** "Friday" for a YYYY-MM-DD day (or an ISO time), in the app's language. */
export function dayName(dayOrIso: string, locale: string): string {
	const date = dayOrIso.length === 10 ? new Date(`${dayOrIso}T12:00:00Z`) : new Date(dayOrIso);
	try {
		return new Intl.DateTimeFormat(locale, {
			weekday: 'long',
			...(dayOrIso.length === 10 ? {timeZone: 'UTC'} : {}),
		}).format(date);
	} catch {
		return new Intl.DateTimeFormat('en-GB', {weekday: 'long'}).format(date);
	}
}

/** "18:00": the person's own clock (24-hour, as the rest of Konvo's cards). */
export function timeOf(iso: string): string {
	return new Intl.DateTimeFormat('en-GB', {hour: '2-digit', minute: '2-digit', hourCycle: 'h23'}).format(new Date(iso));
}

export function tagLabel(i18n: I18n, tag: string): string {
	if (tag === 'Failed today') return i18n._(WRAP_UP_COPY.failedToday);
	if (tag === 'Waiting on you') return i18n._(WRAP_UP_COPY.waitingOnYou);
	return tag;
}

export function startLabel(i18n: I18n, counts: {agents: number; tasks: number}): string {
	if (counts.agents > 0 && counts.tasks > 0) return i18n._(WRAP_UP_COPY.startBoth, counts);
	if (counts.agents > 0) return i18n._(WRAP_UP_COPY.startAgents, counts);
	if (counts.tasks > 0) return i18n._(WRAP_UP_COPY.createTasks, counts);
	return i18n._(WRAP_UP_COPY.nothingTicked);
}

/** One agent's live line after Start. */
export function agentStateLine(i18n: I18n, row: WrapUpAgentRow, now: number): string {
	switch (row.state) {
		case 'working': {
			const minutes = Math.max(
				1,
				Math.round((now - Date.parse(row.started_at ?? new Date(now).toISOString())) / 60_000),
			);
			return minutes >= 60
				? i18n._(WRAP_UP_COPY.workingHours, {hours: Math.floor(minutes / 60), minutes: minutes % 60})
				: i18n._(WRAP_UP_COPY.working, {minutes});
		}
		case 'scheduled':
			return row.start_at ? i18n._(WRAP_UP_COPY.startsAt, {time: timeOf(row.start_at)}) : i18n._(WRAP_UP_COPY.done);
		case 'queued':
			return i18n._(WRAP_UP_COPY.waitsFor, {machine: row.machine_name});
		case 'done':
			return i18n._(WRAP_UP_COPY.done);
		case 'failed':
			return i18n._(WRAP_UP_COPY.didNotStart);
		case 'stopped':
			return i18n._(WRAP_UP_COPY.stopped);
		default:
			return '';
	}
}
